import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PERMISSIONS } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission } from '@repo/shared/helpers';
import DocumentTab from 'src/parts/tabs/DocumentTab';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPartnerCompanyEventDocuments } from 'src/api/partner';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import { constructQuery } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId/documents/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>): { filter?: string; page?: number } => {
		return {
			filter: typeof search.filter === 'string' ? search.filter : undefined,
			page: search.page as number | undefined,
		};
	},
});

function RouteComponent() {
	const { user: authenticatedUser, loading: _userLoading } = useAuthenticatedUser();
	const { partnerId, eventId } = Route.useParams();
	useBreadcrumbLevel(3, 'Documents');
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const queryClient = useQueryClient();

	// Construct query based on filter (similar to admin panel)
	const documentQuery = constructQuery({
		page: searchParams.page || 0,
		all: searchParams.filter === 'all' ? 'yes' : '',
	});

	// load documents for this event
	const { data: documentsResponse, isLoading: documentsLoading } = useQuery({
		queryKey: ['partner-company-event-documents', partnerId, eventId, searchParams],
		queryFn: () => getPartnerCompanyEventDocuments(partnerId, eventId, documentQuery),
	});

	const { data: documentTypesResponse, isLoading: documentTypesLoading } = useQuery({
		queryKey: ['partner-company-document-types', partnerId],
		queryFn: () => getDocumentTypes(constructQuery({ noPagination: 'true', model: 'events' })),
	});

	if (documentsLoading || documentTypesLoading) {
		return <div>Loading...</div>;
	}

	const handleRowClick = (id: string) => {
		navigate({
			to: '/partner/$partnerId/events/$eventId/documents/$documentId',
			params: { partnerId, eventId, documentId: id },
		});
		return null;
	};

	return (
		<>
			<div className="mt-4">
				<DocumentTab
					data={documentsResponse}
					apiUrl={`/api/partner-portal/groups/${partnerId}/events/${eventId}/files`}
					documentTypes={documentTypesResponse?.data || []}
					onReload={() =>
						queryClient.invalidateQueries({ queryKey: ['partner-company-event-documents', partnerId, eventId] })
					}
					revalidateDetail={() => {}}
					onSelectDocument={handleRowClick}
					permissions={{
						metadata: verifyPermission([PERMISSIONS.partnerCompanyDocumentsView]),
						fullView: verifyPermission([PERMISSIONS.partnerCompanyDocumentsView]),
						upload: verifyPermission([PERMISSIONS.partnerCompanyDocumentsCreate]),
						archive: verifyPermission([PERMISSIONS.partnerCompanyDocumentsEdit]),
						edit: verifyPermission([PERMISSIONS.partnerCompanyDocumentsEdit]),
					}}
				/>
			</div>
		</>
	);
}
